import React, { Suspense } from 'react';
import { Route } from 'react-router-dom';
import MainLayout from '../layouts/MainLayout';
import ProtectedRoute from '../components/common/ProtectedRoute';
import SpecialtyProtectedRoute from '../components/common/SpecialtyProtectedRoute';
import SubscriptionProtectedRoute from '../components/common/SubscriptionProtectedRoute';
import AdminProtectedRoute from '../components/common/AdminProtectedRoute';
import LoadingSpinner from '../components/common/LoadingSpinner';

// Lazy load pages
const Home = React.lazy(() => import('../pages/Home'));
const Patients = React.lazy(() => import('../pages/Patients'));
const MedicalRecords = React.lazy(() => import('../pages/MedicalRecords'));
const Dates = React.lazy(() => import('../pages/Dates'));
const Users = React.lazy(() => import('../pages/Users'));
const Specialties = React.lazy(() => import('../pages/Specialties'));
const Institutions = React.lazy(() => import('../pages/Institutions'));
const Roles = React.lazy(() => import('../pages/Roles'));
const Profile = React.lazy(() => import('../pages/Profile'));
const Settings = React.lazy(() => import('../pages/Settings'));
const Subscription = React.lazy(() => import('../pages/Subscription'));
const ChangePasswordPage = React.lazy(() => import('../pages/ChangePasswordPage'));
const ForcedChangePasswordPage = React.lazy(() => import('../pages/ForcedChangePasswordPage'));
const InitialSetupPage = React.lazy(() => import('../pages/InitialSetupPage'));
const ScheduleSettings = React.lazy(() => import('../pages/ScheduleSettings'));
const AppointmentTypesSettings = React.lazy(() => import('../pages/AppointmentTypesSettings'));
const ConsentManagement = React.lazy(() => import('../pages/ConsentManagement'));
const AuditTrail = React.lazy(() => import('../pages/AuditTrail'));
const PatientReconciliation = React.lazy(() => import('../pages/PatientReconciliation'));

const ProtectedRoutes: React.FC = () => {
  return (
    <>
      {/* Dashboard - Requiere especialidad activa para doctores */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <SubscriptionProtectedRoute>
              <MainLayout>
                <SpecialtyProtectedRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <Home />
                  </Suspense>
                </SpecialtyProtectedRoute>
              </MainLayout>
            </SubscriptionProtectedRoute>
          </ProtectedRoute>
        }
      />

      {/* Rutas que requieren especialidad activa */}
      <Route
        path="/patients"
        element={
          <ProtectedRoute>
            <SubscriptionProtectedRoute>
              <MainLayout>
                <SpecialtyProtectedRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <Patients />
                  </Suspense>
                </SpecialtyProtectedRoute>
              </MainLayout>
            </SubscriptionProtectedRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/medical-records"
        element={
          <ProtectedRoute>
            <SubscriptionProtectedRoute>
              <MainLayout>
                <SpecialtyProtectedRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <MedicalRecords />
                  </Suspense>
                </SpecialtyProtectedRoute>
              </MainLayout>
            </SubscriptionProtectedRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/appointments"
        element={
          <ProtectedRoute>
            <SubscriptionProtectedRoute>
              <MainLayout>
                <SpecialtyProtectedRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <Dates />
                  </Suspense>
                </SpecialtyProtectedRoute>
              </MainLayout>
            </SubscriptionProtectedRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/schedule-settings"
        element={
          <ProtectedRoute>
            <SubscriptionProtectedRoute>
              <MainLayout>
                <SpecialtyProtectedRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <ScheduleSettings />
                  </Suspense>
                </SpecialtyProtectedRoute>
              </MainLayout>
            </SubscriptionProtectedRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/appointment-types"
        element={
          <ProtectedRoute>
            <SubscriptionProtectedRoute>
              <MainLayout>
                <SpecialtyProtectedRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <AppointmentTypesSettings />
                  </Suspense>
                </SpecialtyProtectedRoute>
              </MainLayout>
            </SubscriptionProtectedRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/consents"
        element={
          <ProtectedRoute>
            <SubscriptionProtectedRoute>
              <MainLayout>
                <SpecialtyProtectedRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <ConsentManagement />
                  </Suspense>
                </SpecialtyProtectedRoute>
              </MainLayout>
            </SubscriptionProtectedRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/patients/reconciliation"
        element={
          <ProtectedRoute>
            <SubscriptionProtectedRoute>
              <MainLayout>
                <SpecialtyProtectedRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <PatientReconciliation />
                  </Suspense>
                </SpecialtyProtectedRoute>
              </MainLayout>
            </SubscriptionProtectedRoute>
          </ProtectedRoute>
        }
      />

      {/* Rutas de administración - No requieren especialidad activa */}
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <SubscriptionProtectedRoute>
              <AdminProtectedRoute>
                <MainLayout>
                  <Suspense fallback={<LoadingSpinner />}>
                    <Users />
                  </Suspense>
                </MainLayout>
              </AdminProtectedRoute>
            </SubscriptionProtectedRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/specialties"
        element={
          <ProtectedRoute>
            <SubscriptionProtectedRoute>
              <AdminProtectedRoute>
                <MainLayout>
                  <Suspense fallback={<LoadingSpinner />}>
                    <Specialties />
                  </Suspense>
                </MainLayout>
              </AdminProtectedRoute>
            </SubscriptionProtectedRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/institutions"
        element={
          <ProtectedRoute>
            <SubscriptionProtectedRoute>
              <AdminProtectedRoute>
                <MainLayout>
                  <Suspense fallback={<LoadingSpinner />}>
                    <Institutions />
                  </Suspense>
                </MainLayout>
              </AdminProtectedRoute>
            </SubscriptionProtectedRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/roles"
        element={
          <ProtectedRoute>
            <SubscriptionProtectedRoute>
              <AdminProtectedRoute>
                <MainLayout>
                  <Suspense fallback={<LoadingSpinner />}>
                    <Roles />
                  </Suspense>
                </MainLayout>
              </AdminProtectedRoute>
            </SubscriptionProtectedRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/audit"
        element={
          <ProtectedRoute>
            <SubscriptionProtectedRoute>
              <AdminProtectedRoute>
                <MainLayout>
                  <Suspense fallback={<LoadingSpinner />}>
                    <AuditTrail />
                  </Suspense>
                </MainLayout>
              </AdminProtectedRoute>
            </SubscriptionProtectedRoute>
          </ProtectedRoute>
        }
      />

      {/* Rutas de perfil y configuración - No requieren especialidad activa */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <SubscriptionProtectedRoute>
              <MainLayout>
                <Suspense fallback={<LoadingSpinner />}>
                  <Profile />
                </Suspense>
              </MainLayout>
            </SubscriptionProtectedRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SubscriptionProtectedRoute>
              <MainLayout>
                <Suspense fallback={<LoadingSpinner />}>
                  <Settings />
                </Suspense>
              </MainLayout>
            </SubscriptionProtectedRoute>
          </ProtectedRoute>
        }
      />

      {/* Ruta de suscripción - Solo para doctores (no admin) */}
      <Route
        path="/subscription"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Suspense fallback={<LoadingSpinner />}>
                <Subscription />
              </Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Cambio de contraseña autenticado */}
      <Route
        path="/change-password"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <ChangePasswordPage />
            </Suspense>
          </ProtectedRoute>
        }
      />

      {/* Cambio de contraseña forzado (requiresPasswordChange) */}
      <Route
        path="/change-password-required"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <ForcedChangePasswordPage />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route
        path="/initial-setup"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <InitialSetupPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
    </>
  );
};

export default ProtectedRoutes;
